import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import BlogNewsletterSignup from '../components/BlogNewsletterSignup';
import { ArrowLeft, Heart, Facebook, Instagram, Linkedin } from 'lucide-react';

const LibraryTransparency = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Image */}
      <div className="pt-20">
        <div className="relative max-h-[500px] overflow-hidden flex items-center justify-center bg-cream">
          <img
            src="/assets/library-transparency-cover.png"
            alt="Library Transparency Campaign"
            className="w-full h-auto max-h-[500px] object-contain"
            data-testid="library-transparency-hero-image"
          />
        </div>
      </div>

      {/* Content */}
      <article className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-4xl">
          {/* Back Button */}
          <Link
            to="/campaigns"
            className="flex items-center w-fit text-gray-600 hover:text-gold mb-8 transition-colors"
            data-testid="back-to-campaigns-link"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Campaigns
          </Link>

          <span className="block text-gold text-sm md:text-base font-bold tracking-widest uppercase mb-3">
            Take Action
          </span>
          <h1
            className="font-cormorant text-4xl md:text-6xl text-navy mb-4 leading-tight hover-text-zoom"
            data-testid="library-transparency-title"
          >
            Library Transparency
          </h1>
          <div className="w-24 h-1 bg-gold mb-8 rounded-full"></div>

          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
            <h2 className="font-cormorant-garamond text-2xl md:text-3xl text-navy italic">
              Help protect children from explicit material in <span className="text-gold">public spaces</span>
            </h2>

            <p>
              Parents have a right to know what children can access in local libraries, school
              libraries, and other public places.
            </p>

            <p>
              Across Australia, families have raised serious concerns about books placed in
              children&rsquo;s sections that contain graphic sexual content. Some of these books
              are presented as sex education. Yet many go far beyond basic teaching about health,
              safety, puberty, and respect.
            </p>

            <p className="font-cormorant-garamond text-2xl md:text-3xl italic text-navy border-l-4 border-gold pl-6 py-2 my-8">
              Children deserve protection. Parents deserve transparency. Public institutions must
              be accountable.
            </p>

            <p>
              This portal gives local families a way to report books they believe contain
              inappropriate sexual material in areas meant for children.
            </p>

            <div className="flex items-center justify-center gap-4 my-10" aria-hidden="true">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent to-gold"></span>
              <span className="text-gold text-xs font-bold tracking-[0.3em] uppercase">Report Below</span>
              <span className="h-px flex-1 bg-gradient-to-l from-transparent to-gold"></span>
            </div>

            <div className="my-8 rounded-lg overflow-hidden border-t-4 border-gold shadow-md bg-white">
              <iframe
                src="https://docs.google.com/forms/d/e/1FAIpQLSdwN8-H54R3GqB4ZKsQ7XPFPG6xymwqeqHvlhlV7WJ3SBdkHg/viewform?embedded=true"
                width="100%"
                height="1100"
                frameBorder="0"
                marginHeight="0"
                marginWidth="0"
                title="Library Transparency Report Form"
                data-testid="library-transparency-form-iframe"
                className="w-full"
              >
                Loading&hellip;
              </iframe>
            </div>

            <div className="flex items-center justify-center gap-3 my-12" aria-hidden="true">
              <span className="h-px w-16 bg-gold/40"></span>
              <span className="text-gold text-lg">&#9670;</span>
              <span className="h-px w-16 bg-gold/40"></span>
            </div>

            <h2 className="font-cormorant text-3xl md:text-4xl text-navy italic">
              What to <span className="text-gold">report</span>
            </h2>

            <p>
              Please use this portal if you have seen a book in a children&rsquo;s section that
              includes:
            </p>

            <ul className="space-y-3 ml-2">
              <li className="flex items-start">
                <span className="text-gold mr-3 text-xl flex-shrink-0 leading-tight">&#9679;</span>
                <span>graphic sexual descriptions</span>
              </li>
              <li className="flex items-start">
                <span className="text-gold mr-3 text-xl flex-shrink-0 leading-tight">&#9679;</span>
                <span>explicit sexual images</span>
              </li>
              <li className="flex items-start">
                <span className="text-gold mr-3 text-xl flex-shrink-0 leading-tight">&#9679;</span>
                <span>material that appears unsuitable for minors</span>
              </li>
              <li className="flex items-start">
                <span className="text-gold mr-3 text-xl flex-shrink-0 leading-tight">&#9679;</span>
                <span>content placed where children can access it without clear parental knowledge</span>
              </li>
            </ul>

            <p>
              Reports should relate to books found in public libraries, school libraries, community
              facilities, or other publicly funded spaces.
            </p>

            <div className="flex items-center justify-center gap-3 my-12" aria-hidden="true">
              <span className="h-px w-16 bg-gold/40"></span>
              <span className="text-gold text-lg">&#9670;</span>
              <span className="h-px w-16 bg-gold/40"></span>
            </div>

            <h2 className="font-cormorant text-3xl md:text-4xl text-navy italic">
              What happens <span className="text-gold">next</span>
            </h2>

            <p>
              Your report will help identify where these books are being made available to
              children.
            </p>

            <p>
              The aim is simple: shine a light on the issue, gather clear information, and support
              parents who want stronger safeguards for young people.
            </p>

            <p>
              Library staff should always be treated with courtesy. Many staff members do not
              choose which books enter a collection. Please remain calm, respectful, and factual
              when raising concerns.
            </p>

            <div className="bg-cream p-8 rounded-lg my-12 border-l-4 border-gold">
              <p>
                <strong>To submit photos of the book, please email them to:</strong>
                <br />
                <a
                  href="mailto:maryka.groenewald@mp.wa.gov.au"
                  className="text-gold font-semibold hover:underline break-all"
                  data-testid="library-transparency-email-link"
                >
                  maryka.groenewald@mp.wa.gov.au
                </a>
              </p>
              <p className="mt-4 italic text-gray-600">
                Please do not send photos that show people&rsquo;s faces or identifying location
                details. Focus only on the book title, cover, relevant pages, and any
                classification or library label.
              </p>
            </div>

            <BlogNewsletterSignup />

            <div className="border-t border-gray-200 pt-12 mt-12">
              <h3 className="font-cormorant text-2xl text-navy mb-6 text-center">
                Stay Connected!
              </h3>
              <div className="flex justify-center gap-6">
                <a
                  href="https://www.facebook.com/MarykaGroenewaldAC"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-navy flex items-center justify-center text-white hover:scale-110 transition-transform"
                  aria-label="Facebook"
                  data-testid="social-facebook-link"
                >
                  <Facebook size={20} />
                </a>
                <a
                  href="https://www.instagram.com/maryka.groenewald.mlcforwa/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-navy flex items-center justify-center text-white hover:scale-110 transition-transform"
                  aria-label="Instagram"
                  data-testid="social-instagram-link"
                >
                  <Instagram size={20} />
                </a>
                <a
                  href="https://www.linkedin.com/in/marykagroenewald/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-navy flex items-center justify-center text-white hover:scale-110 transition-transform"
                  aria-label="LinkedIn"
                  data-testid="social-linkedin-link"
                >
                  <Linkedin size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

const KeepTheLordsPrayer = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Image */}
      <div className="pt-20">
        <div className="relative max-h-[500px] overflow-hidden flex items-center justify-center bg-cream">
          <img
            src="/assets/lords-prayer-cover.png"
            alt="Keep the Lord's Prayer in Parliament Campaign"
            className="w-full h-auto max-h-[500px] object-contain"
            data-testid="lords-prayer-hero-image"
          />
        </div>
      </div>

      {/* Content */}
      <article className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-4xl">
          {/* Back Button */}
          <Link
            to="/campaigns"
            className="flex items-center w-fit text-gray-600 hover:text-gold mb-8 transition-colors"
            data-testid="back-to-campaigns-link"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Campaigns
          </Link>

          <span className="block text-gold text-sm md:text-base font-bold tracking-widest uppercase mb-3">
            Take Action
          </span>
          <h1
            className="font-cormorant text-4xl md:text-6xl text-navy mb-4 leading-tight hover-text-zoom"
            data-testid="lords-prayer-title"
          >
            Keep the Lord&rsquo;s Prayer in Parliament
          </h1>
          <div className="w-24 h-1 bg-gold mb-8 rounded-full"></div>

          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
            <h2 className="font-cormorant-garamond text-2xl md:text-3xl text-navy italic">
              Stand for our <span className="text-gold">Christian heritage</span>
            </h2>

            <p>
              The Legislative Assembly of Western Australia is currently reviewing its standing
              orders. As part of that review, a Labor MP and an atheist lobby group are pushing
              for the Lord&rsquo;s Prayer to be stripped from the opening of our Parliament.
            </p>

            <p>
              The Lord&rsquo;s Prayer has opened proceedings in WA Parliament for generations. It
              isn&rsquo;t a relic. It is part of who we are, a deliberate acknowledgement of the
              Christian heritage that shaped our laws, our institutions, and the freedoms we
              still enjoy today.
            </p>

            <p className="font-cormorant-garamond text-2xl md:text-3xl italic text-navy border-l-4 border-gold pl-6 py-2 my-8">
              I am proudly supporting the Australian Christians Party&rsquo;s campaign to keep
              the Lord&rsquo;s Prayer in our Parliament &mdash; and I&rsquo;d love for you to
              join us.
            </p>

            <p>
              The committee considering these changes wants to hear from Western Australians.
              That&rsquo;s where you come in. Please use the platform below to send a
              respectful, personal submission urging them to keep the Lord&rsquo;s Prayer where
              it belongs.
            </p>

            {/* Section Label */}
            <div className="flex items-center justify-center gap-4 my-10" aria-hidden="true">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent to-gold"></span>
              <span className="text-gold text-xs font-bold tracking-[0.3em] uppercase">Make a Submission</span>
              <span className="h-px flex-1 bg-gradient-to-l from-transparent to-gold"></span>
            </div>

            {/* Embedded Submission Platform */}
            <div
              className="my-8 rounded-lg overflow-hidden border-t-4 border-gold shadow-md bg-white flex justify-center"
              data-testid="lords-prayer-submission-widget"
            >
              <iframe
                src="https://civipen.overton.solutions/widget/19823d81-a1be-4f1b-9c70-cfcb7ed33c59?outerBackground=%23ffffff&padding=12px"
                style={{ width: '100%', maxWidth: '600px', height: '700px', border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Keep the Lord's Prayer Submission Platform"
              ></iframe>
            </div>

            {/* How to use */}
            <h2 className="font-cormorant text-3xl md:text-4xl text-navy italic mt-12">
              How it <span className="text-gold">works</span>
            </h2>
            <p>It only takes a couple of minutes:</p>
            <ol className="space-y-3 ml-2 list-none">
              <li className="flex items-start">
                <span className="text-gold mr-3 text-xl flex-shrink-0 leading-tight font-bold">1.</span>
                <span>Fill out your details.</span>
              </li>
              <li className="flex items-start">
                <span className="text-gold mr-3 text-xl flex-shrink-0 leading-tight font-bold">2.</span>
                <span>Choose the privacy setting you&rsquo;re comfortable with.</span>
              </li>
              <li className="flex items-start">
                <span className="text-gold mr-3 text-xl flex-shrink-0 leading-tight font-bold">3.</span>
                <span>Add anything personal you&rsquo;d like included in your submission.</span>
              </li>
              <li className="flex items-start">
                <span className="text-gold mr-3 text-xl flex-shrink-0 leading-tight font-bold">4.</span>
                <span>Click <strong>Generate</strong>.</span>
              </li>
              <li className="flex items-start">
                <span className="text-gold mr-3 text-xl flex-shrink-0 leading-tight font-bold">5.</span>
                <span>
                  Review the AI-drafted submission, edit if you&rsquo;d like to make it more your
                  own, then click <strong>Send</strong> &mdash; it will pop straight into your
                  personal email ready to fire off.
                </span>
              </li>
            </ol>

            {/* Decorative Divider */}
            <div className="flex items-center justify-center gap-3 my-12" aria-hidden="true">
              <span className="h-px w-16 bg-gold/40"></span>
              <span className="text-gold text-lg">&#9670;</span>
              <span className="h-px w-16 bg-gold/40"></span>
            </div>

            {/* Troubleshooting */}
            <h2 className="font-cormorant text-3xl md:text-4xl text-navy italic">
              If something <span className="text-gold">goes wrong</span>
            </h2>
            <p>A few common hiccups and how to get past them:</p>

            <div className="space-y-6">
              <div className="bg-cream p-6 rounded-lg border-l-4 border-gold">
                <p className="font-bold text-navy mb-2">
                  The system didn&rsquo;t generate a submission
                </p>
                <p className="text-gray-700 mb-0">
                  Click <strong>Back</strong> and hit <strong>Generate</strong> again &mdash;
                  it will produce a fresh, unique submission for you.
                </p>
              </div>

              <div className="bg-cream p-6 rounded-lg border-l-4 border-gold">
                <p className="font-bold text-navy mb-2">The Send button doesn&rsquo;t work</p>
                <p className="text-gray-700 mb-0">
                  Usually a browser or device security setting. Try a different browser or
                  device, or simply copy the submission text into a new email and send it
                  yourself.
                </p>
              </div>

              <div className="bg-cream p-6 rounded-lg border-l-4 border-gold">
                <p className="font-bold text-navy mb-2">
                  The platform keeps loading but won&rsquo;t create the email
                </p>
                <p className="text-gray-700 mb-0">
                  Usually a busy moment or a flaky connection. Try a different network or
                  give it another go in a few minutes.
                </p>
              </div>
            </div>

            <p className="font-cormorant-garamond text-xl md:text-2xl italic text-navy text-center mt-12">
              Thank you for standing with me on this.
            </p>

            {/* Newsletter Signup */}
            <BlogNewsletterSignup />

            <div className="border-t border-gray-200 pt-12 mt-12">
              <h3 className="font-cormorant text-2xl text-navy mb-6 text-center">
                Stay Connected!
              </h3>
              <div className="flex justify-center gap-6">
                <a
                  href="https://www.facebook.com/MarykaGroenewaldAC"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-navy flex items-center justify-center text-white hover:scale-110 transition-transform"
                  aria-label="Facebook"
                  data-testid="social-facebook-link"
                >
                  <Facebook size={20} />
                </a>
                <a
                  href="https://www.instagram.com/maryka.groenewald.mlcforwa/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-navy flex items-center justify-center text-white hover:scale-110 transition-transform"
                  aria-label="Instagram"
                  data-testid="social-instagram-link"
                >
                  <Instagram size={20} />
                </a>
                <a
                  href="https://www.linkedin.com/in/marykagroenewald/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-navy flex items-center justify-center text-white hover:scale-110 transition-transform"
                  aria-label="LinkedIn"
                  data-testid="social-linkedin-link"
                >
                  <Linkedin size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

const MyChristianSchoolStory = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Image */}
      <div className="pt-20">
        <div className="relative max-h-[500px] overflow-hidden flex items-center justify-center bg-cream">
          <img
            src="/assets/christian-school-stories-cover.png"
            alt="Christian School Stories Campaign"
            className="w-full h-auto max-h-[500px] object-contain"
            data-testid="christian-school-story-hero-image"
          />
        </div>
      </div>

      {/* Back link strip */}
      <div className="px-6 lg:px-12 pt-10">
        <div className="container mx-auto max-w-4xl">
          <Link
            to="/campaigns"
            className="flex items-center w-fit text-gray-600 hover:text-gold transition-colors"
            data-testid="back-to-campaigns-link"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Campaigns
          </Link>
        </div>
      </div>

      {/* Centered intro */}
      <section className="px-6 lg:px-12 pt-10 pb-16">
        <div className="container mx-auto max-w-3xl text-center">
          <Heart className="mx-auto text-gold mb-4" size={48} strokeWidth={1.5} aria-hidden="true" />
          <span className="block text-gold text-sm md:text-base font-bold tracking-widest uppercase mb-4">
            Share Your Story
          </span>
          <h1
            className="font-cormorant text-4xl md:text-6xl text-navy leading-tight hover-text-zoom"
            data-testid="christian-school-story-title"
          >
            Christian School Stories
          </h1>
          <div className="flex items-center justify-center gap-3 mt-6 mb-8" aria-hidden="true">
            <span className="h-px w-20 bg-gold/60"></span>
            <Heart className="text-gold" size={14} fill="currentColor" />
            <span className="h-px w-20 bg-gold/60"></span>
          </div>
          <p className="font-cormorant-garamond text-2xl md:text-3xl text-navy italic leading-relaxed">
            Tell the people considering these changes <span className="text-gold">what a Christian school really means</span> to you, your family or your community.
          </p>
        </div>
      </section>

      {/* Body copy on cream */}
      <section className="px-6 lg:px-12 py-16 bg-cream">
        <div className="container mx-auto max-w-3xl text-gray-700 leading-relaxed space-y-6 text-lg">
          <p>
            Right now, there is real pressure on Christian schools to give up something
            precious &mdash; their freedom to hire staff who genuinely share, live, and pass on
            the faith that shapes the school&rsquo;s mission.
          </p>
          <p>
            That freedom isn&rsquo;t about excluding people. It&rsquo;s about ensuring the
            chapel feels like a chapel, the classroom reflects a Christian worldview, and the
            staff room is a place where teachers can pray together, support one another, and
            model a faith-filled life for the students in their care.
          </p>
          <p>
            Without it, Christian schools risk becoming Christian in name only.
          </p>
          <p className="text-gold font-bold tracking-widest uppercase text-center py-4 text-2xl md:text-3xl">
            That&rsquo;s why your story matters.
          </p>
          <p>
            Whether you&rsquo;re a parent who chose a Christian school for a reason, a former
            student whose teachers shaped your character, a staff member who has poured your
            heart into kids and families, or a grandparent watching the next generation
            grow &mdash; please share what an authentically and distinctively Christian school
            experience has meant to you.
          </p>
          <p>
            Together, these stories paint a picture politicians and bureaucrats simply
            can&rsquo;t ignore.
          </p>
        </div>
      </section>

      {/* Submission widget */}
      <section className="px-6 lg:px-12 py-16 bg-white">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-8">
            <span className="inline-block bg-gold text-white text-xs font-bold tracking-[0.25em] uppercase px-5 py-2 rounded-full">
              Share Your Story Below
            </span>
          </div>

          <div
            className="rounded-xl overflow-hidden shadow-lg ring-1 ring-gold/30 bg-white flex justify-center"
            data-testid="christian-school-story-widget"
          >
            <iframe
              src="https://civipen.overton.solutions/widget/cc84959e-d711-4127-ba81-08714c5e99cb?outerBackground=%23ffffff&padding=12px"
              style={{ width: '100%', maxWidth: '600px', height: '700px', border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Christian School Stories Submission Platform"
            ></iframe>
          </div>
        </div>
      </section>

      {/* How to (3-card grid) */}
      <section className="px-6 lg:px-12 py-16 bg-cream">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <span className="block text-gold text-sm font-bold tracking-widest uppercase mb-3">
              In Three Steps
            </span>
            <h2 className="font-cormorant text-3xl md:text-4xl text-navy italic">
              How to share your story
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow text-center">
              <div className="w-14 h-14 rounded-full bg-gold/10 text-gold flex items-center justify-center mx-auto mb-5 font-cormorant text-3xl font-bold">
                01
              </div>
              <h3 className="font-cormorant text-2xl text-navy mb-3">Fill in your details</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Just a couple of quick details: your name and your email.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow text-center">
              <div className="w-14 h-14 rounded-full bg-gold/10 text-gold flex items-center justify-center mx-auto mb-5 font-cormorant text-3xl font-bold">
                02
              </div>
              <h3 className="font-cormorant text-2xl text-navy mb-3">Add your story</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Describe your Christian School story: a memory, a teacher, etc. Then click <strong>Generate</strong>.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow text-center">
              <div className="w-14 h-14 rounded-full bg-gold/10 text-gold flex items-center justify-center mx-auto mb-5 font-cormorant text-3xl font-bold">
                03
              </div>
              <h3 className="font-cormorant text-2xl text-navy mb-3">Review and send</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Click the big yellow button. Then review and send from your own personal email address!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Troubleshooting (accordion-style summary/details) */}
      <section className="px-6 lg:px-12 py-16 bg-white">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-10">
            <span className="block text-gold text-sm font-bold tracking-widest uppercase mb-3">
              Need a Hand?
            </span>
            <h2 className="font-cormorant text-3xl md:text-4xl text-navy italic">
              Troubleshooting
            </h2>
          </div>

          <div className="space-y-3">
            <details className="group bg-white border border-gold/30 rounded-lg p-5 open:shadow-md transition-shadow">
              <summary className="cursor-pointer font-semibold text-navy flex items-center justify-between list-none">
                <span>The system didn&rsquo;t generate an email</span>
                <span className="text-gold text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-gray-700">
                Click <strong>Back</strong> and hit <strong>Generate</strong> again. It will produce a fresh, unique draft for you.
              </p>
            </details>

            <details className="group bg-white border border-gold/30 rounded-lg p-5 open:shadow-md transition-shadow">
              <summary className="cursor-pointer font-semibold text-navy flex items-center justify-between list-none">
                <span>The Send button doesn&rsquo;t work</span>
                <span className="text-gold text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-gray-700">
                Usually a browser or device security setting is in the way. Try a different browser or device, or simply copy the email text into a new email and send it yourself.
              </p>
            </details>

            <details className="group bg-white border border-gold/30 rounded-lg p-5 open:shadow-md transition-shadow">
              <summary className="cursor-pointer font-semibold text-navy flex items-center justify-between list-none">
                <span>The platform keeps loading but won&rsquo;t create the email</span>
                <span className="text-gold text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-gray-700">
                Usually a busy moment or a flaky internet connection. Try a different network, or give it another go in a few minutes.
              </p>
            </details>
          </div>

          <p className="font-cormorant-garamond text-xl md:text-2xl italic text-navy text-center mt-14">
            Thank you for telling your story.
          </p>
        </div>
      </section>

      {/* Newsletter + socials */}
      <section className="px-6 lg:px-12 pb-20">
        <div className="container mx-auto max-w-4xl">
          <BlogNewsletterSignup />

          <div className="border-t border-gray-200 pt-12 mt-12">
            <h3 className="font-cormorant text-2xl text-navy mb-6 text-center">
              Stay Connected!
            </h3>
            <div className="flex justify-center gap-6">
              <a
                href="https://www.facebook.com/MarykaGroenewaldAC"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-navy flex items-center justify-center text-white hover:scale-110 transition-transform"
                aria-label="Facebook"
                data-testid="social-facebook-link"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://www.instagram.com/maryka.groenewald.mlcforwa/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-navy flex items-center justify-center text-white hover:scale-110 transition-transform"
                aria-label="Instagram"
                data-testid="social-instagram-link"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://www.linkedin.com/in/marykagroenewald/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-navy flex items-center justify-center text-white hover:scale-110 transition-transform"
                aria-label="LinkedIn"
                data-testid="social-linkedin-link"
              >
                <Linkedin size={20} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const CampaignPage = () => {
  const { id } = useParams();

  if (id === 'christian-school-stories') {
    return <MyChristianSchoolStory />;
  }
  if (id === 'library-transparency') {
    return <LibraryTransparency />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center pt-32">
      <div className="text-center">
        <h1 className="font-cormorant text-4xl text-navy mb-4">Campaign Not Found</h1>
        <Button asChild>
          <Link to="/campaigns">Back to Campaigns</Link>
        </Button>
      </div>
    </div>
  );
};

export default CampaignPage;
